import { createContext, ReactNode, useCallback, useContext, useState } from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
}

interface ToastContextValue {
  toast: (options: Omit<Toast, 'id'>) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  warning: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((options: Omit<Toast, 'id'>) => {
    const id = `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setToasts((prev) => [...prev, { ...options, id }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (options: Omit<Toast, 'id'>) => addToast(options),
    [addToast]
  );

  const success = useCallback(
    (title: string, description?: string) =>
      addToast({ type: 'success', title, description }),
    [addToast]
  );

  const error = useCallback(
    (title: string, description?: string) =>
      addToast({ type: 'error', title, description }),
    [addToast]
  );

  const warning = useCallback(
    (title: string, description?: string) =>
      addToast({ type: 'warning', title, description }),
    [addToast]
  );

  const info = useCallback(
    (title: string, description?: string) =>
      addToast({ type: 'info', title, description }),
    [addToast]
  );

  const value: ToastContextValue = {
    toast,
    success,
    error,
    warning,
    info,
  };

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onClose={() => removeToast(t.id)} />
        ))}
        {/* Above the bottom-right corner column (Guide, NADC.AI — CornerStack),
            never over it (2026-10-28). */}
        <ToastPrimitive.Viewport className="fixed bottom-[calc(var(--corner-base,1rem)+var(--corner-lift,0px)+var(--corner-stack-height,0px)+0.5rem)] right-4 flex flex-col gap-2 w-full max-w-sm z-50" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

interface ToastItemProps {
  toast: Toast;
  onClose: () => void;
}

const iconMap = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const colorMap = {
  success: 'border-l-signal-500 bg-signal-500/10',
  error: 'border-l-danger-500 bg-danger-500/10',
  warning: 'border-l-amber-500 bg-amber-500/10',
  info: 'border-l-steel-500 bg-steel-500/10',
};

const iconColorMap = {
  success: 'text-signal-500',
  error: 'text-danger-500',
  warning: 'text-amber-500',
  info: 'text-steel-500',
};

function ToastItem({ toast, onClose }: ToastItemProps) {
  const Icon = iconMap[toast.type];

  return (
    <ToastPrimitive.Root
      className={cn(
        'bg-graphite-900 border border-graphite-800 rounded-lg shadow-panel',
        'border-l-4',
        colorMap[toast.type],
        'p-4 pr-8 relative',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[swipe=end]:animate-out data-[state=closed]:fade-out-80',
        'data-[state=closed]:slide-out-to-right-full',
        'data-[state=open]:slide-in-from-right-full'
      )}
      duration={5000}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className="flex gap-3">
        <Icon className={cn('w-5 h-5 flex-shrink-0', iconColorMap[toast.type])} />
        <div className="flex-1 min-w-0">
          <ToastPrimitive.Title className="text-sm font-medium text-graphite-100">
            {toast.title}
          </ToastPrimitive.Title>
          {toast.description && (
            <ToastPrimitive.Description className="text-sm text-graphite-400 mt-1">
              {toast.description}
            </ToastPrimitive.Description>
          )}
        </div>
      </div>
      <ToastPrimitive.Close
        className="absolute top-4 right-4 text-graphite-500 hover:text-graphite-300 transition-colors"
        aria-label="Close"
      >
        <X className="w-4 h-4" />
      </ToastPrimitive.Close>
    </ToastPrimitive.Root>
  );
}
